import type * as React from 'react';

/** Mutable de-dupe state. Hold this in a ref so it survives React re-renders. */
export interface DedupeGuard {
	firedThisTick: boolean;
}

/**
 * Wrap a `DropdownItem.onClick` so a single activation invokes the handler at
 * most once, even when base-ui dispatches a duplicate click in the same tick.
 *
 * Background: `@base-ui-components/react@1.0.0-beta.6` `useMenuItem` calls
 * `itemRef.current.click()` programmatically inside `onMouseUp`. On some
 * re-render sequences that dispatches a synthetic `click` (isTrusted:false) in
 * the SAME tick as the user's real click (isTrusted:true), so a menu action
 * like "Duplicate" fires twice (a V2 section-clone double-insert).
 *
 * Filtering on `isTrusted` alone would also drop Testing-Library
 * `fireEvent`/`userEvent` clicks (always isTrusted:false in jsdom), making
 * DropdownItem onClick untestable. Instead we de-dupe by tick: the FIRST
 * invocation runs the handler; any further invocation in the same tick is
 * dropped. The guard resets on the next macrotask, so independent activations
 * still fire — and a lone click (real OR test) always runs.
 *
 * The de-dupe state lives in the caller-supplied `guard` so it SURVIVES React
 * re-renders. `DropdownItem` usually gets a fresh inline `onClick` each render,
 * so `useMemo(() => dedupeMenuItemClick(onClick), [onClick])` rebuilds this
 * wrapper constantly. If `firedThisTick` lived in this closure it would reset
 * whenever a render commits BETWEEN base-ui's real click and its same-tick
 * synthetic `click()` — letting the duplicate through. A stable `guard`
 * (from `useRef`) keeps the
 * de-dupe state across rebuilds. When omitted, a fresh per-call guard preserves
 * the simple single-wrapper behavior.
 */
export function dedupeMenuItemClick<E extends React.MouseEvent<HTMLElement>>(
	handler: ((event: E) => void) | undefined,
	guard: DedupeGuard = { firedThisTick: false }
) {
	return (event: E) => {
		if (guard.firedThisTick) {
			return;
		}
		guard.firedThisTick = true;
		setTimeout(() => {
			guard.firedThisTick = false;
		}, 0);
		handler?.(event);
	};
}
